import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { fmtINR, COMPANY } from "@/lib/api";
import { CheckCircle2, Download, ArrowRight } from "lucide-react";

export default function OrderSuccess() {
  const { orderNumber } = useParams();
  const [sp] = useSearchParams();
  const mobile = sp.get("mobile");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (orderNumber && mobile) {
      api.get(`/orders/track/${orderNumber}`, { params: { mobile } }).then((r) => setOrder(r.data)).catch(() => {});
    }
  }, [orderNumber, mobile]);

  const downloadInvoice = () => {
    if (!order) return;
    const html = `<!doctype html><html><head><title>Invoice ${order.order_number}</title>
    <style>body{font-family:sans-serif;padding:32px;color:#0A192F}h1{margin:0}table{width:100%;border-collapse:collapse;margin-top:16px}td,th{border:1px solid #E2E8F0;padding:8px;text-align:left;font-size:14px}
    .right{text-align:right}.total{font-weight:700}</style></head><body>
    <h1>KSPB ENTERPRISES LLP</h1><div>${COMPANY.location} · ${COMPANY.phoneDisplay} · ${COMPANY.email}</div>
    <hr/><h2>Invoice ${order.order_number}</h2>
    <div><b>Billed to:</b> ${order.billing.full_name}, ${order.billing.line1}, ${order.billing.city}, ${order.billing.state} - ${order.billing.pincode}</div>
    <table><thead><tr><th>Item</th><th>Qty</th><th class="right">Price</th><th class="right">Line total</th></tr></thead><tbody>
    ${order.items.map((i) => `<tr><td>${i.name}</td><td>${i.quantity}</td><td class="right">${fmtINR(i.price)}</td><td class="right">${fmtINR(i.line_total)}</td></tr>`).join("")}
    </tbody></table>
    <table style="width:300px;margin-left:auto"><tbody>
    <tr><td>Subtotal</td><td class="right">${fmtINR(order.subtotal)}</td></tr>
    <tr><td>Installation</td><td class="right">${fmtINR(order.installation_total)}</td></tr>
    <tr><td>Subscription</td><td class="right">${fmtINR(order.subscription_total)}</td></tr>
    <tr><td>GST 18%</td><td class="right">${fmtINR(order.gst)}</td></tr>
    ${order.discount ? `<tr><td>Discount</td><td class="right">-${fmtINR(order.discount)}</td></tr>` : ""}
    <tr class="total"><td>Total</td><td class="right">${fmtINR(order.total)}</td></tr>
    </tbody></table>
    <p style="margin-top:24px;color:#64748B;font-size:12px">Thank you for your order. For support contact ${COMPANY.email} or call ${COMPANY.phoneDisplay}.</p>
    </body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html); w.document.close(); w.print();
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center" data-testid="order-success-page">
      <div className="inline-flex w-16 h-16 rounded-full bg-[#00E676]/15 text-[#00E676] items-center justify-center">
        <CheckCircle2 className="w-8 h-8" />
      </div>
      <h1 className="mt-6 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">Thank you for your order!</h1>
      <p className="mt-3 text-slate-600">Your order <span className="font-semibold text-[#0A192F]">{orderNumber}</span> has been recorded. Our team will contact you shortly.</p>

      {order && (
        <div className="mt-8 p-6 rounded-2xl bg-slate-50 border border-slate-200 text-left">
          <div className="flex justify-between items-center">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Order Total</div>
              <div className="font-heading text-2xl font-bold text-[#0A192F]">{fmtINR(order.total)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-slate-500">Payment</div>
              <div className="text-sm font-semibold capitalize">{order.payment_method} · {order.payment_status}</div>
            </div>
          </div>
          <button onClick={downloadInvoice} className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#0A192F] text-white text-sm font-semibold" data-testid="download-invoice">
            <Download className="w-4 h-4" /> Download Invoice
          </button>
        </div>
      )}

      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        <Link to="/track-order" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-slate-200 text-[#0A192F] text-sm font-semibold">Track Order</Link>
        <Link to="/products" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#FF6D00] text-white text-sm font-semibold">Continue Shopping <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </div>
  );
}
